import { baseApi } from './baseApi';
import type { ApiResponse, User, PaginatedResponse, Transaction, Order } from '../../types/api';

interface UsersQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  isBanned?: boolean;
  isAdmin?: boolean;
  sortBy?: 'createdAt' | 'balance' | 'lastActiveAt';
  sortOrder?: 'asc' | 'desc';
}

interface UsersResponse {
  users: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

interface UpdateBalanceRequest {
  amount: number;
  type?: 'admin_add' | 'admin_deduct';
  notes?: string;
}

interface UpdateBalanceResponse {
  user: {
    telegramId: number;
    balance: number;
  };
  message: string;
}

interface BanUserRequest {
  isBanned: boolean;
}

interface BanUserResponse {
  user: {
    telegramId: number;
    isBanned: boolean;
  };
  message: string;
}

interface AdminUserRequest {
  isAdmin: boolean;
}

interface AdminUserResponse {
  user: {
    telegramId: number;
    isAdmin: boolean;
  };
  message: string;
}

interface UserTransactionsParams {
  page?: number;
  limit?: number;
  type?: 'recharge' | 'debit' | 'referral' | 'refund' | 'admin_add' | 'admin_deduct';
  status?: 'pending' | 'completed' | 'failed' | 'reversed';
}

interface UserOrdersParams {
  page?: number;
  limit?: number;
  status?: 'new' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'refunded';
  section?: 'social_ar' | 'social_en' | 'games' | 'manual';
}

export const usersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getUsers: builder.query<ApiResponse<UsersResponse>, UsersQueryParams>({
      query: (params) => ({
        url: '/users',
        method: 'GET',
        params,
      }),
      providesTags: ['User'],
    }),
    getUserById: builder.query<ApiResponse<{ user: User }>, number>({
      query: (id) => ({
        url: `/users/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'User', id }],
    }),
    updateUserBalance: builder.mutation<
      ApiResponse<UpdateBalanceResponse>,
      { id: number; data: UpdateBalanceRequest }
    >({
      query: ({ id, data }) => ({
        url: `/users/${id}/balance`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'User', id },
        'User',
        'Dashboard',
      ],
    }),
    banUser: builder.mutation<ApiResponse<BanUserResponse>, { id: number; data: BanUserRequest }>(
      {
        query: ({ id, data }) => ({
          url: `/users/${id}/ban`,
          method: 'PATCH',
          body: data,
        }),
        invalidatesTags: (_result, _error, { id }) => [{ type: 'User', id }, 'User'],
      }
    ),
    setAdmin: builder.mutation<
      ApiResponse<AdminUserResponse>,
      { id: number; data: AdminUserRequest }
    >({
      query: ({ id, data }) => ({
        url: `/users/${id}/admin`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'User', id }, 'User'],
    }),
    getUserTransactions: builder.query<
      ApiResponse<PaginatedResponse<Transaction>>,
      { id: number; params?: UserTransactionsParams }
    >({
      query: ({ id, params }) => ({
        url: `/users/${id}/transactions`,
        method: 'GET',
        params: params || {},
      }),
      providesTags: (_result, _error, { id }) => [{ type: 'Transaction', id: `user-${id}` }],
    }),
    getUserOrders: builder.query<
      ApiResponse<PaginatedResponse<Order>>,
      { id: number; params?: UserOrdersParams }
    >({
      query: ({ id, params }) => ({
        url: `/users/${id}/orders`,
        method: 'GET',
        params: params || {},
      }),
      providesTags: (_result, _error, { id }) => [{ type: 'Order', id: `user-${id}` }],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetUserByIdQuery,
  useUpdateUserBalanceMutation,
  useBanUserMutation,
  useSetAdminMutation,
  useGetUserTransactionsQuery,
  useGetUserOrdersQuery,
} = usersApi;
